import type { NetMapEdge, NetMapNode } from "./types";

export const NODE_W = 150;
export const NODE_H = 46;

export type Point = { x: number; y: number };

/**
 * Deterministic layout, two zones:
 *  - each connected component is drawn as tiers, left to right: the
 *    most-linked device (the inferred core) on the left, then one column per
 *    BFS hop, each column ordered beside its parents so a NOC reads core →
 *    aggregation → access without crossing lines. Left to right because
 *    ISP chains are deep and narrow and NOC screens are wide. Components stack.
 *  - nodes with no links at all go in a tidy wide grid underneath.
 * Isolated devices are the common case on this deployment (13 of 15 once), and
 * tiling them as one-node components is what made the old map unreadable, hence
 * their own compact grid. Ordering is by node id everywhere, so the picture
 * does not jump between refetches.
 */
export const GRID_DX = NODE_W + 70;
export const GRID_DY = NODE_H + 64;
/** Horizontal distance between tiers: room for an edge label between columns. */
export const TIER_DX = NODE_W + 150;
/** Vertical distance between siblings in one tier. */
export const SLOT_DY = NODE_H + 34;
/** A tier taller than this wraps into sub-columns: big fan-outs grow sideways, where screens have room. */
export const MAX_ROWS = 12;
// Isolated grid is laid out wide rather than square so it matches the canvas
// aspect and the labels survive the default fit.
export const GRID_ASPECT = 4;

/** `roots` (the cores, when known) anchor their component; else the most-linked device does. */
export function layout(nodes: NetMapNode[], edges: NetMapEdge[], roots: Set<number> = new Set()): Map<number, Point> {
  const pos = new Map<number, Point>();
  if (nodes.length === 0) return pos;

  const ids = nodes.map((n) => n.id).sort((a, b) => a - b);
  const adj = new Map<number, number[]>(ids.map((id) => [id, []]));
  for (const e of edges) {
    if (!adj.has(e.source) || !adj.has(e.target) || e.source === e.target) continue;
    adj.get(e.source)!.push(e.target);
    adj.get(e.target)!.push(e.source);
  }
  for (const list of adj.values()) list.sort((a, b) => a - b);

  const lonely = ids.filter((id) => adj.get(id)!.length === 0);
  const taken = new Set<number>(lonely);
  let top = 0, bottom = -Infinity;
  for (const seed of ids) {
    if (taken.has(seed)) continue;
    // Component members, then the most-linked one as the root (ties: lowest id).
    const members = [seed];
    taken.add(seed);
    for (let i = 0; i < members.length; i++) {
      for (const peer of adj.get(members[i])!) {
        if (!taken.has(peer)) { taken.add(peer); members.push(peer); }
      }
    }
    const root = members.find((id) => roots.has(id)) ?? members.reduce((best, id) =>
      adj.get(id)!.length > adj.get(best)!.length ? id : best, members[0]);

    // Tiers by BFS hop; each column sorted by the mean slot of its parents
    // (barycentre), which is what keeps a tree free of crossings.
    const slot = new Map<number, number>([[root, 0]]);
    const tiers: number[][] = [[root]];
    const seen = new Set([root]);
    for (let frontier = [root]; frontier.length; ) {
      const next: number[] = [];
      for (const id of frontier) for (const peer of adj.get(id)!) {
        if (!seen.has(peer)) { seen.add(peer); next.push(peer); }
      }
      const bary = (id: number) => {
        const ps = adj.get(id)!.filter((q) => slot.has(q)).map((q) => slot.get(q)!);
        return ps.reduce((a, b) => a + b, 0) / ps.length;
      };
      next.sort((a, b) => bary(a) - bary(b) || a - b);
      next.forEach((id, i) => slot.set(id, i - (next.length - 1) / 2));
      if (next.length) tiers.push(next);
      frontier = next;
    }
    const shape = tiers.map((t) => {
      const cols = Math.ceil(t.length / MAX_ROWS);
      return { cols, rows: Math.ceil(t.length / cols) };
    });
    const height = Math.max(...shape.map((sh) => sh.rows));
    const cy = top + ((height - 1) * SLOT_DY) / 2;
    let x = 0;
    tiers.forEach((tier, depth) => {
      const { cols, rows } = shape[depth];
      // Column-major, so barycentre neighbours stay next to each other.
      tier.forEach((id, i) => {
        const col = Math.floor(i / rows), row = i % rows;
        pos.set(id, { x: x + col * GRID_DX, y: cy + (row - (rows - 1) / 2) * SLOT_DY });
      });
      x += (cols - 1) * GRID_DX + TIER_DX;
    });
    bottom = top + (height - 1) * SLOT_DY;
    top = bottom + SLOT_DY * 2;
  }

  if (lonely.length) {
    const cols = Math.min(lonely.length, Math.max(1, Math.round(Math.sqrt(lonely.length * GRID_ASPECT))));
    const y0 = bottom === -Infinity ? 0 : bottom + GRID_DY * 1.25;
    lonely.forEach((id, i) => {
      pos.set(id, { x: (i % cols) * GRID_DX, y: y0 + Math.floor(i / cols) * GRID_DY });
    });
  }
  return pos;
}


/**
 * Operator-dragged positions win over the auto-layout; nodes without one keep
 * their computed slot, nudged down out of any saved box so a freshly mirrored
 * device never appears underneath a placed one.
 * ponytail: the nudge only avoids SAVED boxes, so two nudged nodes can still
 * land on each other — drag them, that is the point of saving positions.
 */
export function applySaved(
  auto: Map<number, Point>,
  nodes: { id: number; key: string }[],
  saved: Map<string, Point>,
): Map<number, Point> {
  const out = new Map(auto);
  if (saved.size === 0) return out;
  const fixed: Point[] = [];
  for (const n of nodes) {
    const s = saved.get(n.key);
    if (s) { out.set(n.id, s); fixed.push(s); }
  }
  if (fixed.length === 0) return out;
  const hits = (q: Point) => fixed.some((f) => Math.abs(f.x - q.x) < NODE_W && Math.abs(f.y - q.y) < NODE_H);
  for (const n of nodes) {
    if (saved.has(n.key)) continue;
    let q = out.get(n.id);
    if (!q) continue;
    for (let i = 0; i < 50 && hits(q); i++) q = { x: q.x, y: q.y + NODE_H + 14 };
    out.set(n.id, q);
  }
  return out;
}
